import { useEffect, useState } from 'react';
import { stripFiles } from 'helpers/formDraft';

// Keeps a react-hook-form's values as a draft in this browser (see
// helpers/formDraft). `pendingDraft` is a draft found on opening the page
// that the user hasn't chosen to continue or discard yet; while it is shown,
// autosave is paused so it isn't overwritten before they answer.
//
//   const { pendingDraft, draftSavedAt, continueDraft, discardDraft } =
//     useFormDraft({ store, scope: user.id, enabled: !isEdit, values, reset, getValues });
export default function useFormDraft({
  store,
  scope,
  enabled = true,
  values,
  reset,
  getValues
}) {
  const [pendingDraft, setPendingDraft] = useState(null);
  const [draftSavedAt, setDraftSavedAt] = useState(null);

  useEffect(() => {
    setPendingDraft(enabled ? store.load(scope) : null);
  }, [enabled, scope, store]);

  // Auto-save while typing (debounced)
  const draftJson = enabled ? JSON.stringify(stripFiles(values)) : '';
  useEffect(() => {
    if (!enabled || pendingDraft || !store.hasContent(values)) return;
    const timer = setTimeout(() => {
      setDraftSavedAt(store.save(scope, values));
    }, 800);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftJson, enabled, pendingDraft, scope, store]);

  // Typing into the fresh form instead of answering the prompt counts as
  // "discard": the new entries take over as the draft
  useEffect(() => {
    if (pendingDraft && store.hasContent(values)) setPendingDraft(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftJson]);

  const continueDraft = () => {
    // Keep current defaults for anything the draft doesn't have (e.g. files)
    reset({ ...getValues(), ...pendingDraft.values });
    setDraftSavedAt(pendingDraft.savedAt);
    setPendingDraft(null);
  };

  const discardDraft = () => {
    store.clear(scope);
    setPendingDraft(null);
  };

  return { pendingDraft, draftSavedAt, continueDraft, discardDraft };
}
